import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { StepButton, StepLabel } from '@mui/material';
import {
  fetchUser
} from '#action/user/settings';
import { updateBreadcrumb } from '#action/breadcrumb';
import { CLEAR_USER_DATA } from '#action/types';
import CustomStepIcon from '#component/shipment/form/custom-step-icon';
import PreloaderComponent from '#component/preloader';
import { whitelabel } from '#config/whitelabel';
import { StyledSection, StyledStep, StyledStepper } from './styles';
import UserProfileForm from './user-profile';
import UserNotificationsForm from './notifications';
import UserSecurityForm from './security';
import UserApiKeyOverview from './api-key';
import { setDocumentTitle } from '#helper';

const isAPIKeySectionViewable =
  !import.meta.env.VITE_USER_API_EDIT_SCREEN_BLACK_LIST?.split(',')?.includes(
    whitelabel.getName()
  );

const steps = [
  { label: 'User profile', form: 'UserSettingsForm' },
  { label: 'Notifications', form: 'UserNotificationsForm' },
  { label: 'Security', form: 'UserSecurityForm' }
];

if (isAPIKeySectionViewable) steps.push({ label: 'API keys', form: 'UserApiKeysForm' });

const SettingsUserForm = props => {
  const { match } = props;
  const dispatch = useDispatch();
  const user = useSelector(s => s.user, shallowEqual);
  const account = useSelector(s => s.account, shallowEqual);
  const [step, setStep] = useState(0);
  const formPristine = {};
  const shallDisplaySecurity = user.id === account.id;

  useEffect(() => {
    dispatch(fetchUser(match.params.id));
  }, [dispatch, match.params.id]);

  useEffect(() => {
    if (user.firstName && user.lastName) {
      setDocumentTitle('User profile', `${user.firstName} ${user.lastName}`);
      const breadcrumbs = [];

      if (account.userType < 10) {
        breadcrumbs.push({ label: 'Users', path: '/user' }, { label: `${user.firstName} ${user.lastName}` });
      }

      dispatch(updateBreadcrumb(breadcrumbs));
    }
  }, [dispatch, account.userType, user.firstName, user.lastName]);

  useEffect(() => () => {
    dispatch({
      type: CLEAR_USER_DATA
    });
  }, [dispatch]);

  const handleStepClick = stepIndex => () => {
    setStep(stepIndex);
  };

  const securityFormInitialValues = useMemo(() => ({
    twoFactorAuth: user.twoFactorAuth,
    password: '',
    confirmPassword: ''
  }), [user.twoFactorAuth]);

  if (['idle', 'loading'].includes(user.requestStatus)) {
    return <PreloaderComponent />;
  }

  return (
    <StyledSection>
      {shallDisplaySecurity && (
        <StyledStepper
          alternativeLabel
          nonLinear
          activeStep={step}
        >
          {steps.map(
            (section, index) => {
              if (!section) return null;

              const { label, form } = section;
              return (
                <StyledStep key={label} completed={formPristine[form] ?? true}>
                  <StepButton onClick={handleStepClick(index)}>
                    <StepLabel StepIconComponent={CustomStepIcon}>
                      <span style={{ fontWeight: index === step ? 500 : 400 }}>
                        {label}
                      </span>
                    </StepLabel>
                  </StepButton>
                </StyledStep>
              );
            }
          )}
        </StyledStepper>
      )}
      {step === 0 &&
        <UserProfileForm
          {...props}
          mainCntStyle={{ marginTop: 0, padding: 0 }}
        />}
      {step === 1 &&
        <UserNotificationsForm {...props} />}
      {step === 2 &&
        <UserSecurityForm
          {...props}
          initialValues={securityFormInitialValues}
        />}
      {isAPIKeySectionViewable ?
        step === 3 && <UserApiKeyOverview {...props} /> :
        null}
    </StyledSection>
  );
};

export default SettingsUserForm;
